
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle, AlertTriangle, Loader2, Settings } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getEdgeFunctionUrl } from '@/utils/supabaseUrl';
import { 
  isBenchmarkBotConfigured, 
  setupBenchmarkBotWebhook, 
  getBenchmarkBotWebhookInfo 
} from '@/services/telegram/benchmarkBotService';

interface WebhookInfo {
  url: string;
  is_active: boolean;
  pending_update_count?: number;
  last_error_date?: number;
  last_error_message?: string;
}

export const BenchmarkBotWebhookStatus: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState<WebhookInfo | null>(null);
  const [isBotConfigured, setIsBotConfigured] = useState(false);
  const { toast } = useToast();

  const checkBotConfiguration = async () => {
    const configured = await isBenchmarkBotConfigured();
    setIsBotConfigured(configured);
    return configured;
  };

  const checkWebhookStatus = async () => {
    setIsLoading(true);
    try {
      const configured = await checkBotConfiguration();
      if (!configured) {
        setWebhookInfo(null);
        return;
      }

      const info = await getBenchmarkBotWebhookInfo();
      setWebhookInfo(info);
    } catch (error: any) {
      console.error('Error checking webhook status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to check webhook status",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setupWebhook = async () => {
    setIsSettingUp(true);
    try {
      const configured = await checkBotConfiguration();
      if (!configured) {
        toast({
          title: "Bot Token Required",
          description: "Benchmark bot token is not configured",
          variant: "destructive",
        });
        return;
      }

      const result = await setupBenchmarkBotWebhook();
      
      if (result.success) {
        toast({
          title: "Webhook Setup Successful",
          description: "Benchmark bot webhook has been configured",
        });
        await checkWebhookStatus();
      } else {
        toast({
          title: "Webhook Setup Failed",
          description: result.error || "Failed to setup webhook",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Failed to setup webhook:', error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSettingUp(false);
    }
  };

  useEffect(() => {
    checkWebhookStatus();
  }, []);

  const getStatusAlert = () => {
    if (!isBotConfigured) {
      return (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Bot Token Required</AlertTitle>
          <AlertDescription>
            The benchmark bot token is not configured. Please configure it first.
          </AlertDescription>
        </Alert>
      );
    }

    if (!webhookInfo) {
      return (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Webhook Status Unknown</AlertTitle>
          <AlertDescription>
            Unable to get webhook information. Click "Check Status" to try again.
          </AlertDescription>
        </Alert>
      );
    }

    if (webhookInfo.is_active) {
      return (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Webhook Active</AlertTitle>
          <AlertDescription>
            Your benchmark bot webhook is properly configured and active.
            {webhookInfo.pending_update_count && webhookInfo.pending_update_count > 0 && (
              <div className="mt-2">
                Pending updates: {webhookInfo.pending_update_count}
              </div>
            )}
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Webhook Not Active</AlertTitle>
        <AlertDescription>
          Your benchmark bot webhook is not set up. Click "Setup Webhook" to configure it.
          {webhookInfo.last_error_message && (
            <div className="mt-2 text-sm">
              Last error: {webhookInfo.last_error_message}
            </div>
          )}
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Benchmark Bot Webhook Status
        </CardTitle>
        <CardDescription>
          Check and manage the webhook connection for the benchmark bot
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {getStatusAlert()}
        
        {webhookInfo && (
          <div className="space-y-2">
            <div className="text-sm">
              <strong>Webhook URL:</strong> {webhookInfo.url || 'Not set'}
            </div>
            <div className="text-sm">
              <strong>Status:</strong> {webhookInfo.is_active ? 'Active' : 'Inactive'}
            </div>
            <div className="text-sm">
              <strong>Expected URL:</strong> {getEdgeFunctionUrl('telegram-benchmark-bot')}
            </div>
          </div>
        )}
        
        <div className="flex gap-2">
          <Button 
            onClick={checkWebhookStatus} 
            variant="outline" 
            disabled={isLoading || isSettingUp}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : (
              'Check Status'
            )}
          </Button>
          
          <Button 
            onClick={setupWebhook} 
            disabled={isLoading || isSettingUp || !isBotConfigured}
          >
            {isSettingUp ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Setting Up...
              </>
            ) : (
              'Setup Webhook'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
